import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/lib/auth";
import { Sidebar, MobileHeader } from "@/components/dashboard/Sidebar";
import { InstallPrompt } from "@/components/marketing/InstallPrompt";
import { WelcomeModal, hasSeenWelcome } from "@/components/onboarding/WelcomeModal";
import { HelpButton } from "@/components/onboarding/HelpButton";
import DashboardPage from "@/pages/DashboardPage";
import SourcesPage from "@/pages/SourcesPage";
import PropertiesPage from "@/pages/PropertiesPage";
import RunsPage from "@/pages/RunsPage";
import CoveragePage from "@/pages/CoveragePage";
import AlertsPage from "@/pages/AlertsPage";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import PricingPage from "@/pages/PricingPage";
import AuthCallback from "@/pages/AuthCallback";
import GettingStartedPage from "@/pages/GettingStartedPage";
import ImportsPage from "@/pages/ImportsPage";
import WebhooksPage from "@/pages/WebhooksPage";
import HotDealsPage from "@/pages/HotDealsPage";
import { BillingSuccessPage, BillingCancelPage } from "@/pages/BillingResult";
import FeaturesPage from "@/pages/marketing/FeaturesPage";
import AboutPage from "@/pages/marketing/AboutPage";
import ContactPage from "@/pages/marketing/ContactPage";
import TermsPage from "@/pages/marketing/TermsPage";
import PrivacyPage from "@/pages/marketing/PrivacyPage";
import BlogPage from "@/pages/marketing/BlogPage";
import BlogPostPage from "@/pages/marketing/BlogPostPage";
import { MarketingShell } from "@/components/marketing/MarketingShell";

const TOAST_OPTIONS = {
  className:
    "font-display border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] rounded-none",
};

function Shell({ children }) {
  const { user } = useAuth();
  const [welcomeOpen, setWelcomeOpen] = React.useState(false);

  // First-run onboarding: auto-open the welcome modal once per device for
  // authenticated users. The HelpButton lets anyone reopen it later.
  React.useEffect(() => {
    if (user && !hasSeenWelcome()) {
      const t = window.setTimeout(() => setWelcomeOpen(true), 900);
      return () => window.clearTimeout(t);
    }
  }, [user]);

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-honeycomb">
      <Sidebar />
      <MobileHeader />
      <main className="flex-1 min-w-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
          {children}
        </div>
      </main>
      {/* Signed-in subscribers should also get the install nudge —
          especially on mobile where the home-screen shortcut is the
          fastest way to open Lien Scout. */}
      <InstallPrompt />
      <HelpButton />
      <WelcomeModal open={welcomeOpen} onOpenChange={setWelcomeOpen} />
    </div>
  );
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

/** Root shows marketing landing for anon visitors, dashboard for signed-in users. */
function RootDispatch() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Shell><DashboardPage /></Shell>;
  return <LandingPage />;
}

/** Coverage is linked from both the app sidebar AND the public marketing footer.
 *  Anonymous visitors get the marketing shell; signed-in users get the app shell. */
function CoverageDispatch() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Shell><CoveragePage /></Shell>;
  return (
    <MarketingShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <CoveragePage />
      </div>
    </MarketingShell>
  );
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public marketing routes */}
      <Route path="/" element={<RootDispatch />} />
      <Route path="/welcome" element={<LandingPage />} />
      <Route path="/promo" element={<LandingPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/legal/terms" element={<TermsPage />} />
      <Route path="/legal/privacy" element={<PrivacyPage />} />
      <Route path="/privacypolicy" element={<PrivacyPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/billing/success" element={<BillingSuccessPage />} />
      <Route path="/billing/cancel" element={<BillingCancelPage />} />

      {/* App (authenticated) routes */}
      <Route path="/dashboard" element={<Shell><DashboardPage /></Shell>} />
      <Route path="/sources" element={<Shell><SourcesPage /></Shell>} />
      <Route path="/properties" element={<Shell><PropertiesPage /></Shell>} />
      <Route path="/imports" element={<Shell><ImportsPage /></Shell>} />
      <Route path="/coverage" element={<CoverageDispatch />} />
      <Route path="/alerts" element={<Shell><AlertsPage /></Shell>} />
      <Route path="/runs" element={<Shell><RunsPage /></Shell>} />
      <Route path="/getting-started" element={<Shell><GettingStartedPage /></Shell>} />
      <Route path="/hot-deals" element={<Shell><HotDealsPage /></Shell>} />
      <Route path="/admin/webhooks" element={<Shell><WebhooksPage /></Shell>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster
            position="top-right"
            toastOptions={TOAST_OPTIONS}
          />
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
